import { defineStore } from "pinia";
import { useSessionStore } from "@/stores/sessionStore";
import RepositoryFactory from "@/repositories/apiGateWay/repositoryFactory";
const apiGatewayRepository = RepositoryFactory.get("apiGateway");
import ApplicationError from "@/helpers/applicationError";
import { getCreditCardBrand, handleError, loadPaygentScript } from "@/helpers/helpers";
import {
    API_RESPONSE_SUCCESS, BAD_REQUEST_ERROR_CODE,
    HTTP_CODE_CREATED,
    HTTP_CODE_SUCCESS
} from "@/data/constants";

export const useCreditCardStore = defineStore("creditCards", {
    id: "creditCard",
    state: () => {
        return {
            isLoading: false,
            creditCards: [],
            creditCardForm: {
                credit_card_id: null,
                card_number: null,
                cvv: null,
                expiration_date: null,
                holder_name: null,
                card_brand_code: null,
            },
            creditCardFormErrors: {},
            creditCardFormErrorsMsg: null,
            paygentTokenModule: {
                merchant_id: null,
                token_generation_key: null,
            },
            ErrorMessage: ""
        };
    },
    getters: {
        getUserInfo() {
            return useSessionStore().session_data;
        },
        getCreditCardFormInputs(state) {
            return state.creditCardForm;
        }
    },
    actions: {
        resetCreditCardForm() {
            this.creditCardFormErrors = {}
            this.creditCardFormErrorsMsg = null
            this.creditCardForm = {}
        },
        async getPaygentTokenModule() {
            try {
                this.isLoading = true;

                const response = await apiGatewayRepository.getPaygentTokenModule();
                if (response.data != null && response.success == API_RESPONSE_SUCCESS) {
                    this.paygentTokenModule = response.data;
                }
                this.isLoading = false;
            } catch (e) {
                this.isLoading = false;
                const errorMessage = await handleError(e);
                if (errorMessage != null) {
                    this.ErrorMessage = errorMessage;
                }
                throw new ApplicationError(errorMessage, {}, e);
            }
        },
        async getCreditCards() {
            try {
                this.isLoading = true;

                const response = await apiGatewayRepository.getCreditCards();
                if (response.data != null && response.success == API_RESPONSE_SUCCESS) {
                    this.creditCards = response.data;
                }
                this.isLoading = false;
            } catch (e) {
                this.isLoading = false;
                const errorMessage = await handleError(e);
                if (errorMessage != null) {
                    this.ErrorMessage = errorMessage;
                }
                throw new ApplicationError(errorMessage, {}, e);
            }
        },
        async deleteCreditCard(creditCardId) {
            try {
                this.isLoading = true;
                const userInfo = this.getUserInfo;
                const params = {
                    user_id: Number(userInfo.hubsynch_id).toString(),
                    credit_card_id: creditCardId.toString()
                };

                await apiGatewayRepository.deleteCreditCard(params);

                this.isLoading = false;
            } catch (e) {
                this.isLoading = false;
                this.ErrorMessage = await handleError(e);

                throw new ApplicationError(e.message, creditCardId, e)
            }
        },
        async registerCreditCard() {
            try {
                this.isLoading = true;

                await this.generateCreatePaygentToken();
                return true;
            } catch (error) {
                this.isLoading = false;
                if (error.response.data != undefined && error.response.data && error.response.data.errors) {
                    const data = error.response.data;

                    if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "object") {
                        this.creditCardFormErrors = data.errors;
                    }

                    if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "string") {
                        this.creditCardFormErrorsMsg = data.errors;
                    }

                    return false;
                }
                throw new ApplicationError(error.message, {}, error);
            }
        },
        async hubsynchStoreCreditCard(response) {
            let tokenResponse = response;

            if (typeof tokenResponse.result != "undefined" && tokenResponse.result == "0000") {
                try {
                    var creditCardCreateData = {};
                    creditCardCreateData.card_paygent_finger_print = tokenResponse.tokenizedCardObject.fingerprint;
                    creditCardCreateData.card_paygent_masked_card_number = tokenResponse.tokenizedCardObject.masked_card_number;
                    creditCardCreateData.card_paygent_token = tokenResponse.tokenizedCardObject.token;
                    creditCardCreateData.card_paygent_valid_until = tokenResponse.tokenizedCardObject.valid_until;
                    creditCardCreateData.card_paygent_hash = tokenResponse.hc;

                    creditCardCreateData.card_brand_id = this.creditCardForm.card_brand_code;
                    creditCardCreateData.card_month = this.creditCardForm.expiration_date.slice(0, 2).toString();
                    creditCardCreateData.card_year = '20' + this.creditCardForm.expiration_date.slice(3, 5).toString();
                    creditCardCreateData.default_flag = "false";

                    const userInfo = this.getUserInfo;
                    creditCardCreateData.user_id = userInfo.hubsynch_id.toString();
                    response = await apiGatewayRepository.createCreditCard(creditCardCreateData);
                    if (response.code === HTTP_CODE_CREATED) {
                        this.resetCreditCardForm();
                        await this.getCreditCards();
                        this.isLoading = false;
                    }
                } catch (error) {
                    this.isLoading = false;
                    if (error.response.data && error.response.data.errors) {
                        const data = error.response.data;

                        if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "object") {
                            this.creditCardFormErrors = data.errors;
                        }

                        if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "string") {
                            this.creditCardFormErrorsMsg = data.errors;
                        }

                        return false;
                    }
                    throw new ApplicationError(error.message, {}, error);
                }
            } else {
                let responseCode = tokenResponse.result;
                this.creditCardFormErrorsMsg = responseCode;
                this.isLoading = false;
            }
        },
        async updateCreditCard() {
            try {
                this.isLoading = true;
                await this.generateUpdatePaygentToken();
                return true;
            } catch (error) {
                this.isLoading = false;
                if (error.response.data && error.response.data.errors) {
                    const data = error.response.data;

                    if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "object") {
                        this.creditCardFormErrors = data.errors;
                    }

                    if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "string") {
                        this.creditCardFormErrorsMsg = data.errors;
                    }

                    return false;
                }
                throw new ApplicationError(error.message, {}, error);
            }
        },
        async hubsynchUpdateCreditCard(response) {
            let tokenResponse = response;

            if (typeof tokenResponse.result != "undefined" && tokenResponse.result == "0000") {
                try {
                    var creditCardUpdateData = {};
                    creditCardUpdateData.card_paygent_finger_print = tokenResponse.tokenizedCardObject.fingerprint;
                    creditCardUpdateData.card_paygent_masked_card_number = tokenResponse.tokenizedCardObject.masked_card_number;
                    creditCardUpdateData.card_paygent_token = tokenResponse.tokenizedCardObject.token;
                    creditCardUpdateData.card_paygent_valid_until = tokenResponse.tokenizedCardObject.valid_until;
                    creditCardUpdateData.card_paygent_hash = tokenResponse.hc;

                    creditCardUpdateData.card_brand_id = this.creditCardForm.card_brand_code;
                    creditCardUpdateData.credit_card_id = this.creditCardForm.credit_card_id.toString();
                    creditCardUpdateData.card_month = this.creditCardForm.expiration_date.slice(0, 2).toString();
                    creditCardUpdateData.card_year = '20' + this.creditCardForm.expiration_date.slice(3, 5).toString();
                    creditCardUpdateData.default_flag = "false";

                    const userInfo = this.getUserInfo;
                    creditCardUpdateData.user_id = userInfo.hubsynch_id.toString();

                    response = await apiGatewayRepository.updateCreditCard(creditCardUpdateData);
                    if (response.code === HTTP_CODE_SUCCESS) {
                        this.resetCreditCardForm();
                        await this.getCreditCards();
                        this.isLoading = false;
                    }
                } catch (error) {
                    this.isLoading = false;
                    if (error.response.data && error.response.data.errors) {
                        const data = error.response.data;

                        if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "object") {
                            this.creditCardFormErrors = data.errors;
                        }

                        if (data.code === BAD_REQUEST_ERROR_CODE && typeof data.errors === "string") {
                            this.creditCardFormErrorsMsg = data.errors;
                        }
                    }
                    throw new ApplicationError(error.message, {}, error);
                }
            } else {
                let responseCode = tokenResponse.result;
                this.creditCardFormErrorsMsg = responseCode;
                this.isLoading = false;
            }
        },
        async generateCreatePaygentToken() {
            let data = this.getCreditCardFormInputs;
            this.creditCardForm.card_brand_code = getCreditCardBrand(data.card_number);
            // remove spaces between card numbers
            let cardNumber = data.card_number.replace(/\s/g, '');
            const PaygentToken = await loadPaygentScript();
            const paygent = new PaygentToken();
            let merchantId = this.paygentTokenModule.merchant_id;
            let tokenGenerateKey = this.paygentTokenModule.token_generation_key;
            let formatYear = data.expiration_date.slice(3, 5).toString();
            let cardMonth = data.expiration_date.slice(0, 2).toString();
            paygent.createToken(merchantId, tokenGenerateKey, {
                card_number: cardNumber,
                expire_year: formatYear,
                expire_month: cardMonth,
                cvc: data.cvv,
                name: data.holder_name,
            }, this.hubsynchStoreCreditCard);
        },
        async generateUpdatePaygentToken() {
            let data = this.getCreditCardFormInputs;
            this.creditCardForm.card_brand_code = getCreditCardBrand(data.card_number);
            const PaygentToken = await loadPaygentScript();
            const paygent = new PaygentToken();
            let merchantId = this.paygentTokenModule.merchant_id;
            let tokenGenerateKey = this.paygentTokenModule.token_generation_key;
            paygent.createCvcToken(merchantId, tokenGenerateKey, {
                cvc: data.cvv,
            }, this.hubsynchUpdateCreditCard);
        }
    },
});
